/**
 * @file useWeatherData.js
 * @description This file contains custom React hooks for fetching and managing weather data from different sources.
 * It provides hooks for current weather data, last week's weather data, and temporary data(Testing of new stations).
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { stationMetaData } from "../utils/rawData";

// API Configuration
const API_BASE_URL = "https://client-api.silanga.ca/aws";
const ECCC_API_BASE_URL = "https://client-api.silanga.ca/ECCC";
const API_KEY = process.env.REACT_APP_API_TOKEN;
const TEMP_API_BASE_URL = "https://client-api.silanga.ca/temp"; // temporary data endpoint (Testing of new stations)
const NTI_API_BASE_URL = "https://www.smartatlantic.ca/erddap/tabledap"; // NTI SmartAtlantic API
const TIDE_API_BASE_URL = "https://client-api.silanga.ca/tide-prediction";
const LUNAR_SOLAR_API_BASE_URL = "https://client-api.silanga.ca/lunar-solar";
/**
 * Fetches data from NTI SmartAtlantic API and transforms it to match the application's data structure
 * @param {string} stationId - The station ID (UID)
 * @param {boolean} lastWeek - Whether to fetch last week's data
 * @returns {Promise<Object|Array>} - Transformed data for the application
 */
export const fetchNTIData = async (stationId, lastWeek = false) => {
  try {
    const endpoint = lastWeek
      ? `${NTI_API_BASE_URL}/${stationId}.jsonlKVP?&time%3E=max(time)-7days&time%3C=max(time)&orderByDescending(%22time%22)`
      : `${NTI_API_BASE_URL}/${stationId}.jsonlKVP?&time%3E=max(time)-1hour&orderByDescending(%22time%22)`;

    const response = await axios.get(endpoint);

    // The response is a string with one or more JSON objects, each on a new line
    // Split the string by newlines and parse the first JSON object
    const responseData = response.data.trim().split("\n");
    const firstDataObj = JSON.parse(responseData[0]);

    if (lastWeek) {
      // For last week data, parse all objects
      const parsedData = responseData.map((line) => {
        const dataObj = JSON.parse(line);
        return transformNTIData(dataObj);
      });
      return parsedData;
    } else {
      // For current data, just return the first (most recent) data point
      return [transformNTIData(firstDataObj)];
    }
  } catch (error) {
    console.error(`Error fetching NTI data for station ${stationId}:`, error);
    throw error;
  }
};

/**
 * Transforms NTI data to match the application's data structure
 * @param {Object} ntiData - Raw data from NTI API
 * @returns {Object} - Transformed data for the application
 */
export const transformNTIData = (ntiData) => {
  // Create a data structure that matches what the application expects
  const timestamp = ntiData.time;

  /**
   * Maps NTI QC value to application QC value
   * NTI QC values:
   * -1: Missing/Invalid - Data is missing, invalid, or not evaluated
   *  0: Not Checked - Data has not been QC-checked or status is unknown
   *  1: Pass/Good - Data has passed QC checks and is considered good/valid
   *
   * App QC values:
   * 100: Good data
   *   0: Error/bad data
   */
  const mapQCValue = (qcStatus) => {
    if (qcStatus === 1) return 100; // Pass/Good -> 100 (Good)
    if (qcStatus === -1) return -10; // Missing/Invalid -> 0 (Error)
    return null; // Not Checked
  };

  return {
    timestamp,
    elements: {
      temperature_ext: {
        value: ntiData.air_temp_avg !== undefined ? ntiData.air_temp_avg : null,
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
      humidity_ext: {
        value: ntiData.rel_humidity !== undefined ? ntiData.rel_humidity : null,
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
      wind_speed: {
        value: ntiData.wind_spd_avg !== undefined ? ntiData.wind_spd_avg : null,
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
      wind_direction: {
        value: ntiData.wind_dir_avg !== undefined ? ntiData.wind_dir_avg : null,
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
      wind_gust_speed: {
        value:
          (ntiData?.wind_gust_avg || ntiData?.wind_spd_max) !== undefined
            ? ntiData?.wind_gust_avg || ntiData?.wind_spd_max
            : null,
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
      wind_gust_direction: {
        value: ntiData.wind_dir_avg !== undefined ? ntiData.wind_dir_avg : null, // Using wind_dir_avg as there's no separate gust direction
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
      surface_air_pressure: {
        value:
          ntiData.air_pressure_avg !== undefined
            ? ntiData.air_pressure_avg
            : null,
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
      // Map other values if available
      wind_chill: {
        value: ntiData.wind_chill !== undefined ? ntiData.wind_chill : null,
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
      heat_index: {
        value: ntiData.heat_index !== undefined ? ntiData.heat_index : null,
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
      air_dewpoint: {
        value:
          (ntiData?.air_dewpoint_avg || ntiData?.dew_point) !== undefined
            ? ntiData?.air_dewpoint_avg || ntiData?.dew_point
            : null,
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
      snow_depth: {
        // it is in meter so converted to mm
        value:
          ntiData?.snow_depth !== undefined ? ntiData.snow_depth * 1000 : null,
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
      precipitation_rate: {
        value: ntiData.precip_rate !== undefined ? ntiData.precip_rate : null,
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
      precipitation_total: {
        value: ntiData.precip_total !== undefined ? ntiData.precip_total : null,
        qc_value:
          ntiData.qc_status !== undefined
            ? mapQCValue(ntiData.qc_status)
            : undefined,
      },
    },
  };
};

/**
 * Custom hook to fetch and manage current weather data for a specific station
 * @param {string} stationId - The unique identifier of the weather station
 * @returns {Object} An object containing:
 *   - currentData: The current weather data for the station
 *   - loading: Boolean indicating if data is being fetched
 *   - error: Any error that occurred during fetching
 */
export const useCurrentWeatherData = (stationId) => {
  const [currentData, setCurrentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetches current weather data for the specified station
   * @async
   */
  const fetchCurrentData = async () => {
    if (!stationId) {
      setLoading(false);
      return;
    }
    // Check if station is operational
    const station = stationMetaData.find((s) => s.id === stationId);
    if (!station || station.status !== "Operational") {
      setError("Station is not operational");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let data;

      if (station.type === "NTI_Weather_Station") {
        // Fetch data from NTI API
        const response = await fetchNTIData(stationId);
        data = response[0]; // Get the first (most recent) data point
      } else {
        // Handle ECCC and regular stations as before
        const baseUrl =
          station.type === "ECCC_Weather_Station"
            ? ECCC_API_BASE_URL
            : API_BASE_URL;
        const response = await axios.get(baseUrl, {
          params: { uid: stationId },
          headers: { "x-api-key": API_KEY },
        });
        data = response.data[0];
      }

      console.log(data);
      setCurrentData(data);
    } catch (err) {
      setError("Error fetching current weather data.");
      setCurrentData(null);
      console.error("Error fetching current weather data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentData();

    // Schedule hourly refetch
    const scheduleNextRefetch = () => {
      const now = new Date();
      const nextRefetchTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours() + 1,
        3, // 3 minutes past the hour
        0
      );

      return setTimeout(() => {
        fetchCurrentData();
        scheduleNextRefetch();
      }, nextRefetchTime - now);
    };

    const timeoutId = scheduleNextRefetch();
    return () => clearTimeout(timeoutId);
  }, [stationId]);

  return { currentData, loading, error };
};

/**
 * Custom hook to fetch and manage last week's weather data for a specific station
 * @param {string} stationId - The unique identifier of the weather station
 * @returns {Object} An object containing:
 *   - lastWeekData: The weather data for the last week
 *   - weekDataLoading: Boolean indicating if data is being fetched
 *   - weekDataError: Any error that occurred during fetching
 */
export const useLastWeekWeatherData = (stationId) => {
  const [lastWeekData, setLastWeekData] = useState(null);
  const [weekDataLoading, setLoading] = useState(true);
  const [weekDataError, setError] = useState(null);

  /**
   * Fetches last week's weather data for the specified station
   * @async
   */
  const fetchLastWeekData = async () => {
    if (!stationId) {
      setLoading(false);
      return;
    }
    // Check if station is operational
    const station = stationMetaData.find((s) => s.id === stationId);
    if (!station || station.status !== "Operational") {
      setError("Station is not operational");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let data;

      if (station.type === "NTI_Weather_Station") {
        // Fetch data from NTI API
        data = await fetchNTIData(stationId, true);
      } else {
        // Handle ECCC and regular stations as before
        const baseUrl =
          station.type === "ECCC_Weather_Station"
            ? ECCC_API_BASE_URL
            : API_BASE_URL;
        const response = await axios.get(baseUrl, {
          params: { uid: stationId, last_week: true },
          headers: { "x-api-key": API_KEY },
        });
        data = response.data;
      }

      setLastWeekData(data);
    } catch (err) {
      // setError("Error fetching last week weather data.");
      setLastWeekData(null);
      console.error("Error fetching last week weather data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLastWeekData();

    // Schedule hourly refetch
    const scheduleNextRefetch = () => {
      const now = new Date();
      const nextRefetchTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours() + 1,
        3, // 3 minutes past the hour
        0
      );

      return setTimeout(() => {
        fetchLastWeekData();
        scheduleNextRefetch();
      }, nextRefetchTime - now);
    };

    const timeoutId = scheduleNextRefetch();
    return () => clearTimeout(timeoutId);
  }, [stationId]);

  return { lastWeekData, weekDataLoading, weekDataError };
};

/**
 * Custom hook to fetch and manage temporary data for a specific station
 * @param {string} stationId - The unique identifier of the weather station
 * @returns {Object} An object containing:
 *   - tempData: The temporary data for the station
 *   - loading: Boolean indicating if data is being fetched
 *   - error: Any error that occurred during fetching
 */
export const useTemporaryData = (stationId) => {
  const [tempData, setTempData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetches temporary data for the specified station
   * @async
   */
  const fetchTempData = async () => {
    if (!stationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(TEMP_API_BASE_URL, {
        params: {
          uid: stationId,
          type: "latest",
        },
        headers: { "x-api-key": API_KEY },
      });

      setTempData(response.data);
    } catch (err) {
      console.error("API Error:", err.response || err);
      setError("Error fetching temporary data.");
      setTempData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTempData();
  }, [stationId]);

  return { tempData, loading, error };
};

/**
 * Custom hook to fetch and manage tide prediction data for a specific station
 * @param {string} stationId - The unique identifier of the weather station
 * @returns {Object} An object containing:
 *   - tideData: The tide prediction data for the station
 *   - loading: Boolean indicating if data is being fetched
 *   - error: Any error that occurred during fetching
 */
export const useCurrentTidePredictionData = (stationId) => {
  const [tideData, setTideData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetches tide prediction data for the specified station
   * @async
   */
  const fetchTideData = async () => {
    if (!stationId) {
      setLoading(false);
      return;
    }

    // Check if station is operational
    const station = stationMetaData.find((s) => s.id === stationId);
    if (
      !station ||
      station.status !== "Operational"
      // || station.type !== "WIRL_Weather_Station"
    ) {
      setError("Station is not operational");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get current UTC time minus 1 day to include past data
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000); // Subtract 24 hours
      const startTime = oneDayAgo.toISOString().slice(0, 19).replace("T", " ");

      const response = await axios.get(TIDE_API_BASE_URL, {
        params: {
          uid: stationId,
          start_time: startTime,
        },
        headers: { "x-api-key": API_KEY },
      });

      console.log("Tide prediction data:", response.data);
      setTideData(response.data);
    } catch (err) {
      setError("Error fetching tide prediction data.");
      setTideData(null);
      console.error("Error fetching tide prediction data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTideData();

    // Schedule hourly refetch
    const scheduleNextRefetch = () => {
      const now = new Date();
      const nextRefetchTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours() + 1,
        3, // 3 minutes past the hour
        0
      );

      return setTimeout(() => {
        fetchTideData();
        scheduleNextRefetch();
      }, nextRefetchTime - now);
    };

    const timeoutId = scheduleNextRefetch();
    return () => clearTimeout(timeoutId);
  }, [stationId]);

  return { tideData, loading, error };
};

/**
 * Custom hook to fetch and manage lunar-solar data for a specific location
 * @param {string} stationId - The unique identifier of the weather station
 * @returns {Object} An object containing:
 *   - lunarSolarData: The lunar-solar data for the location
 *   - loading: Boolean indicating if data is being fetched
 *   - error: Any error that occurred during fetching
 */
// export const useLunarSolarData = (stationId) => {
//   const [lunarSolarData, setLunarSolarData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   /**
//    * Fetches lunar-solar data for the specified station
//    * @async
//    */
//   const fetchLunarSolarData = async () => {
//     if (!stationId) {
//       setLoading(false);
//       return;
//     }

//     // Check if station is operational
//     const station = stationMetaData.find((s) => s.id === stationId);
//     if (!station || station.status !== "Operational") {
//       setError("Station is not operational");
//       setLoading(false);
//       return;
//     }

//     setLoading(true);
//     setError(null);

//     try {
//       const response = await axios.get(LUNAR_SOLAR_API_BASE_URL, {
//         params: {
//           longitude: station.default_longitude,
//           latitude: station.default_latitude,
//         },
//         headers: { "x-api-key": API_KEY },
//       });
//       console.log("Lunar-solar data:", response.data);
//       setLunarSolarData(response.data);
//     } catch (err) {
//       setError("Error fetching lunar-solar data.");
//       setLunarSolarData(null);
//       console.error("Error fetching lunar-solar data:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchLunarSolarData();

//     // Schedule hourly refetch
//     const scheduleNextRefetch = () => {
//       const now = new Date();
//       const nextRefetchTime = new Date(
//         now.getFullYear(),
//         now.getMonth(),
//         now.getDate(),
//         now.getHours() + 1,
//         3, // 3 minutes past the hour
//         0
//       );

//       return setTimeout(() => {
//         fetchLunarSolarData();
//         scheduleNextRefetch();
//       }, nextRefetchTime - now);
//     };

//     const timeoutId = scheduleNextRefetch();
//     return () => clearTimeout(timeoutId);
//   }, [stationId]);

//   return { lunarSolarData, loading, error };
// };

{
  /*uncomment this code to use the all weather data hook*/
}
// export const useAllWeatherData = (stationId, limit, offset) => {
// 	const [allData, setAllData] = useState(null);
// 	const [allDataTotalRecords, setallDataTotalRecords] = useState(0);
// 	const [allDataLoading, setLoading] = useState(true);
// 	const [allDataError, setError] = useState(null);

// 	useEffect(() => {
// 		const fetchAllData = async () => {
// 			if (!stationId) {
// 				setLoading(false);
// 				return;
// 			}

// 			setLoading(true);
// 			setError(null);

// 			try {
// 				const response = await axios.get(API_BASE_URL, {
// 					params: {
// 						uid: stationId,
// 						all_data: true,
// 						limit: limit,
// 						offset: offset,
// 					},
// 					headers: {
// 						"x-api-key": API_KEY,
// 					},
// 				});
// 				setAllData(response.data.data);
// 				setallDataTotalRecords(response.data.pagination.total_records);
// 				if (response.data.statusCode === 400) {
// 					// setError(JSON.parse(response.data.body).error + JSON.parse(response.data.body).details)
// 					setError(JSON.parse(response.data.body).error);
// 				}
// 			} catch (err) {
// 				setError("Error fetching All weather data.");
// 				setAllData(null);
// 				console.error("Error fetching All weather data:", err);
// 			} finally {
// 				setLoading(false);
// 			}
// 		};

// 		fetchAllData();
// 	}, [stationId, limit, offset]);

// 	return { allData, allDataLoading, allDataError, allDataTotalRecords };
// };
